import { useEffect, useRef } from 'react';
import { Filter } from 'lucide-react';
import {
  ACTIVITY_STATUS_OPTIONS,
  PLAY_TYPE_OPTIONS,
  PREP_NEEDED_OPTIONS,
  DURATION_OPTIONS,
  AGE_GROUP_OPTIONS,
  type ActivityFilters,
  type Tag,
  type Transition,
  type ActivityCategory,
} from '../types/activity';

interface FilterControlsProps {
  filters: ActivityFilters;
  onFilterChange: (key: keyof ActivityFilters, value: ActivityFilters[keyof ActivityFilters]) => void;
  availableTags: Tag[];
  isLoadingTags?: boolean;
  availableTransitions: Transition[];
  isLoadingTransitions?: boolean;
  availableCategories: ActivityCategory[];
  isLoadingCategories?: boolean;
}

export function FilterControls({
  filters,
  onFilterChange,
  availableTags,
  isLoadingTags = false,
  availableTransitions,
  isLoadingTransitions = false,
  availableCategories,
  isLoadingCategories = false,
}: FilterControlsProps) {
  const tagsRef = useRef<HTMLDetailsElement>(null);
  const periodsRef = useRef<HTMLDetailsElement>(null);
  const ageGroupsRef = useRef<HTMLDetailsElement>(null);
  const categoriesRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      for (const ref of [tagsRef, periodsRef, ageGroupsRef, categoriesRef]) {
        if (ref.current && !ref.current.contains(e.target as Node)) {
          ref.current.open = false;
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleTagToggle = (tagId: number) => {
    const currentTags = filters.tagIds || [];
    const newTags = currentTags.includes(tagId)
      ? currentTags.filter((id) => id !== tagId)
      : [...currentTags, tagId];
    onFilterChange('tagIds', newTags);
  };

  const handleTransitionToggle = (transitionId: number) => {
    const currentTransitions = filters.transitionIds || [];
    const newTransitions = currentTransitions.includes(transitionId)
      ? currentTransitions.filter((id) => id !== transitionId)
      : [...currentTransitions, transitionId];
    onFilterChange('transitionIds', newTransitions);
  };

  const handleCategoryToggle = (categoryId: number) => {
    const current = filters.categoryIds || [];
    const next = current.includes(categoryId)
      ? current.filter((id) => id !== categoryId)
      : [...current, categoryId];
    onFilterChange('categoryIds', next);
  };

  const handleAgeGroupToggle = (key: string) => {
    const currentKeys = filters.ageGroupKeys || [];
    const newKeys = currentKeys.includes(key)
      ? currentKeys.filter((k) => k !== key)
      : [...currentKeys, key];
    onFilterChange('ageGroupKeys', newKeys);
  };

  return (
    <div className="space-y-4">
      {/* Filter Header */}
      <div className="flex items-center gap-2 text-admin-slate-500">
        <Filter className="w-4 h-4" />
        <span className="text-body-sm font-medium">Filters</span>
      </div>

      {/* Filter Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 xl:grid-cols-8 gap-4">
        {/* Status Filter */}
        <div>
          <label className="block text-body-sm text-admin-slate-500 mb-2">
            Status
          </label>
          <select
            value={filters.status}
            onChange={(e) => onFilterChange('status', e.target.value)}
            className="w-full px-3 py-2 bg-admin-dark-800 border border-admin-slate-300/10 rounded-md text-admin-slate-400 focus:outline-none focus:ring-2 focus:ring-admin-accent-blue transition-colors"
          >
            <option value="">All Statuses</option>
            {ACTIVITY_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Play Type Filter */}
        <div>
          <label className="block text-body-sm text-admin-slate-500 mb-2">
            Play Type
          </label>
          <select
            value={filters.playType}
            onChange={(e) => onFilterChange('playType', e.target.value)}
            className="w-full px-3 py-2 bg-admin-dark-800 border border-admin-slate-300/10 rounded-md text-admin-slate-400 focus:outline-none focus:ring-2 focus:ring-admin-accent-blue transition-colors"
          >
            <option value="">All Types</option>
            {PLAY_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Prep Needed Filter */}
        <div>
          <label className="block text-body-sm text-admin-slate-500 mb-2">
            Prep Needed
          </label>
          <select
            value={filters.prepNeeded}
            onChange={(e) => onFilterChange('prepNeeded', e.target.value)}
            className="w-full px-3 py-2 bg-admin-dark-800 border border-admin-slate-300/10 rounded-md text-admin-slate-400 focus:outline-none focus:ring-2 focus:ring-admin-accent-blue transition-colors"
          >
            <option value="">All Options</option>
            {PREP_NEEDED_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Duration Filter */}
        <div>
          <label className="block text-body-sm text-admin-slate-500 mb-2">
            Duration
          </label>
          <select
            value={filters.duration}
            onChange={(e) => onFilterChange('duration', e.target.value ? parseInt(e.target.value, 10) : '')}
            className="w-full px-3 py-2 bg-admin-dark-800 border border-admin-slate-300/10 rounded-md text-admin-slate-400 focus:outline-none focus:ring-2 focus:ring-admin-accent-blue transition-colors"
          >
            <option value="">All Durations</option>
            {DURATION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Category Filter */}
        <div>
          <label className="block text-body-sm text-admin-slate-500 mb-2">
            Category {filters.categoryIds.length > 0 && `(${filters.categoryIds.length})`}
          </label>
          <div className="relative">
            <details ref={categoriesRef} className="group">
              <summary className="cursor-pointer list-none w-full px-3 py-2 bg-admin-dark-800 border border-admin-slate-300/10 rounded-md text-admin-slate-400 focus:outline-none focus:ring-2 focus:ring-admin-accent-blue transition-colors flex justify-between items-center">
                <span>
                  {filters.categoryIds.length > 0
                    ? `${filters.categoryIds.length} selected`
                    : 'All Categories'}
                </span>
                <svg
                  className="w-4 h-4 transition-transform group-open:rotate-180"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </summary>
              <div className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto bg-admin-dark-800 border border-admin-slate-300/10 rounded-md shadow-lg">
                {isLoadingCategories ? (
                  <div className="p-3 text-center text-admin-slate-500 text-body-sm">
                    Loading...
                  </div>
                ) : availableCategories.length === 0 ? (
                  <div className="p-3 text-center text-admin-slate-500 text-body-sm">
                    No categories available
                  </div>
                ) : (
                  availableCategories.map((cat) => (
                    <label
                      key={cat.id}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-admin-dark-700 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={filters.categoryIds.includes(parseInt(cat.id))}
                        onChange={() => handleCategoryToggle(parseInt(cat.id))}
                        className="w-4 h-4 rounded border-admin-slate-300/20 bg-admin-dark-900 text-admin-accent-blue focus:ring-2 focus:ring-admin-accent-blue focus:ring-offset-0"
                      />
                      {cat.color && (
                        <span
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: cat.color }}
                        />
                      )}
                      <span className="text-body-sm text-admin-slate-400">
                        {cat.name}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </details>
          </div>
        </div>

        {/* Tags Filter (Multi-select with checkboxes) */}
        <div>
          <label className="block text-body-sm text-admin-slate-500 mb-2">
            Tags {filters.tagIds.length > 0 && `(${filters.tagIds.length})`}
          </label>
          <div className="relative">
            <details ref={tagsRef} className="group">
              <summary className="cursor-pointer list-none w-full px-3 py-2 bg-admin-dark-800 border border-admin-slate-300/10 rounded-md text-admin-slate-400 focus:outline-none focus:ring-2 focus:ring-admin-accent-blue transition-colors flex justify-between items-center">
                <span>
                  {filters.tagIds.length > 0
                    ? `${filters.tagIds.length} selected`
                    : 'All Tags'}
                </span>
                <svg
                  className="w-4 h-4 transition-transform group-open:rotate-180"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </summary>
              <div className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto bg-admin-dark-800 border border-admin-slate-300/10 rounded-md shadow-lg">
                {isLoadingTags ? (
                  <div className="p-3 text-center text-admin-slate-500 text-body-sm">
                    Loading tags...
                  </div>
                ) : availableTags.length === 0 ? (
                  <div className="p-3 text-center text-admin-slate-500 text-body-sm">
                    No tags available
                  </div>
                ) : (
                  availableTags.map((tag) => (
                    <label
                      key={tag.id}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-admin-dark-700 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={filters.tagIds.includes(parseInt(tag.id))}
                        onChange={() => handleTagToggle(parseInt(tag.id))}
                        className="w-4 h-4 rounded border-admin-slate-300/20 bg-admin-dark-900 text-admin-accent-blue focus:ring-2 focus:ring-admin-accent-blue focus:ring-offset-0"
                      />
                      <span className="text-body-sm text-admin-slate-400">
                        {tag.label}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </details>
          </div>
        </div>

        {/* Time Periods Filter (Multi-select with checkboxes) */}
        <div>
          <label className="block text-body-sm text-admin-slate-500 mb-2">
            Time Periods {filters.transitionIds.length > 0 && `(${filters.transitionIds.length})`}
          </label>
          <div className="relative">
            <details ref={periodsRef} className="group">
              <summary className="cursor-pointer list-none w-full px-3 py-2 bg-admin-dark-800 border border-admin-slate-300/10 rounded-md text-admin-slate-400 focus:outline-none focus:ring-2 focus:ring-admin-accent-blue transition-colors flex justify-between items-center">
                <span>
                  {filters.transitionIds.length > 0
                    ? `${filters.transitionIds.length} selected`
                    : 'All Periods'}
                </span>
                <svg
                  className="w-4 h-4 transition-transform group-open:rotate-180"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </summary>
              <div className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto bg-admin-dark-800 border border-admin-slate-300/10 rounded-md shadow-lg">
                {isLoadingTransitions ? (
                  <div className="p-3 text-center text-admin-slate-500 text-body-sm">
                    Loading...
                  </div>
                ) : availableTransitions.length === 0 ? (
                  <div className="p-3 text-center text-admin-slate-500 text-body-sm">
                    No time periods available
                  </div>
                ) : (
                  availableTransitions.map((transition) => (
                    <label
                      key={transition.id}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-admin-dark-700 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={filters.transitionIds.includes(parseInt(transition.id))}
                        onChange={() => handleTransitionToggle(parseInt(transition.id))}
                        className="w-4 h-4 rounded border-admin-slate-300/20 bg-admin-dark-900 text-amber-500 focus:ring-2 focus:ring-amber-500 focus:ring-offset-0"
                      />
                      <span className="text-body-sm text-admin-slate-400">
                        {transition.label}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </details>
          </div>
        </div>

        {/* Age Groups Filter (Multi-select with checkboxes) */}
        <div>
          <label className="block text-body-sm text-admin-slate-500 mb-2">
            Age Groups {filters.ageGroupKeys.length > 0 && `(${filters.ageGroupKeys.length})`}
          </label>
          <div className="relative">
            <details ref={ageGroupsRef} className="group">
              <summary className="cursor-pointer list-none w-full px-3 py-2 bg-admin-dark-800 border border-admin-slate-300/10 rounded-md text-admin-slate-400 focus:outline-none focus:ring-2 focus:ring-admin-accent-blue transition-colors flex justify-between items-center">
                <span>
                  {filters.ageGroupKeys.length > 0
                    ? `${filters.ageGroupKeys.length} selected`
                    : 'All Ages'}
                </span>
                <svg
                  className="w-4 h-4 transition-transform group-open:rotate-180"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </summary>
              <div className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto bg-admin-dark-800 border border-admin-slate-300/10 rounded-md shadow-lg">
                {AGE_GROUP_OPTIONS.map((option) => (
                  <label
                    key={option.key}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-admin-dark-700 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={filters.ageGroupKeys.includes(option.key)}
                      onChange={() => handleAgeGroupToggle(option.key)}
                      className="w-4 h-4 rounded border-admin-slate-300/20 bg-admin-dark-900 text-admin-accent-blue focus:ring-2 focus:ring-admin-accent-blue focus:ring-offset-0"
                    />
                    <span className="text-body-sm text-admin-slate-400">
                      {option.label} yrs
                    </span>
                  </label>
                ))}
              </div>
            </details>
          </div>
        </div>

        {/* AI Generated Filter */}
        <div>
          <label className="block text-body-sm text-admin-slate-500 mb-2">
            AI Generated
          </label>
          <label className="flex items-center gap-2 px-3 py-2 bg-admin-dark-800 border border-admin-slate-300/10 rounded-md cursor-pointer hover:border-admin-slate-300/20 transition-colors">
            <input
              type="checkbox"
              checked={filters.isAiGenerated}
              onChange={(e) => onFilterChange('isAiGenerated', e.target.checked)}
              className="w-4 h-4 rounded border-admin-slate-300/20 bg-admin-dark-900 text-violet-500 focus:ring-2 focus:ring-violet-500 focus:ring-offset-0"
            />
            <span className="text-body-sm text-admin-slate-400">Only AI</span>
          </label>
        </div>
      </div>
    </div>
  );
}
